<template>
  <div class="rpt-overseas-chain">
    <div class="report-shell">
      <header class="report-header">
        <div class="header-row">
          <div>
            <h1>卡奥斯海外链群收入</h1>
            <p class="subtitle">按业务链群展示 H1 海外收入规模、收入占比、目标完成率和同比增幅，识别全球化经营的覆盖与差距。</p>
          </div>
          <div class="header-actions">
            <span class="report-badge"><i class="fa fa-clock-o"></i> 更新至 2026-07-24</span>
            <!-- <button type="button" class="analysis-button" @click="requestAnalysis"><i class="fa fa-magic"></i><span>智能分析</span></button> -->
          </div>
        </div>
      </header>

      <section class="kpi-grid">
        <article v-for="(kpi, i) in kpis" :key="i" class="kpi-card">
          <div class="kpi-label">{{ kpi.label }}</div>
          <div class="kpi-value" :style="{ color: toneColor(kpi.tone) }">{{ kpi.value }}</div>
          <div class="kpi-note">{{ kpi.note }}</div>
        </article>
      </section>

      <section class="table-card">
        <div class="table-card-head">
          <div>
            <div class="section-title"><i class="fa fa-globe"></i><span>H1海外链群收入分析</span></div>
            <div class="section-note">金额单位：万元；完成率、占比、增幅均取接口返回值。</div>
          </div>
          <div class="legend">
            <span><i class="legend-dot" style="background:#0aa66a"></i>完成率 ≥ 100%</span>
            <span><i class="legend-dot" style="background:#4a5ff3"></i>90% ≤ 完成率 &lt; 100%</span>
            <span><i class="legend-dot" style="background:#e54b3d"></i>完成率 &lt; 90%</span>
          </div>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th rowspan="2" class="col-chain">链群</th><th rowspan="2" class="col-num">H1整体收入<br>（万元）</th>
                <th colspan="4" class="col-group">H1海外收入（万元）</th><th rowspan="2" class="col-eval">评价</th>
              </tr>
              <tr>
                <th>海外收入额</th><th>海外收入占比</th><th>完成率</th><th>增幅</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i">
                <td class="chain-name">{{ row.name }}</td>
                <td class="numeric">{{ num(row.total) }}</td>
                <td class="numeric">{{ num(row.overseas) }}</td>
                <td>{{ row.overseasRatio.toFixed(1) }}%</td>
                <td class="rate-cell">
                  <span class="rate-fill" :style="{width: Math.min(row.completionRate, 100).toFixed(1) + '%', background: stateColor(row)}"></span>
                  <span class="rate-text" :style="{color: stateColor(row)}">{{ row.completionRate.toFixed(1) }}%</span>
                </td>
                <td :style="{color: row.yoy >= 0 ? '#0aa66a' : '#e54b3d', fontWeight: 800}">{{ growthFmt(row) }}</td>
                <td><div class="evaluation"><i class="status-light" :style="{background: stateColor(row)}"></i><span :style="{color: stateColor(row)}">{{ stateLabel(row) }}</span></div></td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="7" style="padding: 24px; color: #94a3b8; text-align: center;">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="report-foot">
          <span>数据来源：海外项目台账、CRM、ERP 收入确认数据。</span>
          <span>管理口径：海外收入为境外项目确认收入；仅展示接口返回的有数据链群。</span>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const CHAIN_NAMES = ['智能制造', '智能产线', '机器视觉', '化工行业', '工赋平台', '智慧能源', '碳索能源', 'AIOT解决方案']

export default {
  name: 'OverseasChainRevenueReport',
  data() {
    return { rows: [], loading: false }
  },
  async created() {
    await this.fetchData()
  },
  computed: {
    totalIncome() { return this.rows.reduce(function (s, r) { return s + Number(r.total || 0) }, 0) },
    overseasIncome() { return this.rows.reduce(function (s, r) { return s + Number(r.overseas || 0) }, 0) },
    overseasTarget() { return this.rows.reduce(function (s, r) { return s + Number(r.target || 0) }, 0) },
    kpis() {
      var coverage = CHAIN_NAMES.length ? (this.rows.length / CHAIN_NAMES.length * 100) : 0
      var completionRate = this.overseasTarget ? (this.overseasIncome / this.overseasTarget * 100) : 0
      return [
        { label: 'H1 整体收入', value: this.num(this.totalIncome) + ' 万', note: this.rows.length + ' 个链群汇总', tone: 'primary' },
        { label: 'H1 海外收入', value: this.num(this.overseasIncome) + ' 万', note: '海外收入占比 ' + (this.totalIncome ? (this.overseasIncome / this.totalIncome * 100).toFixed(1) : '0.0') + '%', tone: 'warning' },
        { label: '海外收入完成率', value: completionRate.toFixed(1) + '%', note: '海外目标 ' + this.num(this.overseasTarget) + ' 万', tone: 'negative' },
        { label: '有数据链群', value: this.rows.length + ' 个', note: '数据覆盖率 ' + coverage.toFixed(1) + '%', tone: 'negative' }
      ]
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        var res = await axios.post(
          'https://bigdata.cosmoplat.com/prod-api/system/api/call/cosmoplat/overseas/revenue',
          { secret: '88py6X', paramList: [{ paramName: 'year', paramValue: '2026' }] },
          { headers: { 'Content-Type': 'application/json' }}
        )
        var data = (res.data && res.data.data) || []
        this.rows = data.map(function (item) {
          return {
            name: item.chain_group || '—',
            total: Number(item.h1_total_revenue || 0),
            overseas: Number(item.overseas_revenue || 0),
            target: Number(item.revenue_target || 0),
            overseasRatio: Number(item.overseas_revenue_ratio || 0),
            completionRate: Number(item.completion_rate || 0),
            yoy: item.yoy != null ? Number(item.yoy) : null
          }
        })
      } catch (e) {
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    num(v) { return Math.round(Number(v || 0)).toLocaleString('zh-CN') },
    toneColor(t) { var m = { primary: '#4a5ff3', positive: '#0aa66a', warning: '#d98213', negative: '#e54b3d' }; return m[t] || '#4a5ff3' },
    stateColor(row) { var r = row.completionRate; return r >= 100 ? '#0aa66a' : r >= 90 ? '#4a5ff3' : '#e54b3d' },
    stateLabel(row) { var r = row.completionRate; return r >= 100 ? '领先' : r >= 90 ? '基本达成' : row.overseas ? '承压' : '未形成收入' },
    growthFmt(row) { if (row.yoy == null) return '—'; return (row.yoy >= 0 ? '+' : '') + row.yoy.toFixed(1) + '%' },
    requestAnalysis() {
      var topRow = this.rows.slice().sort(function (a, b) { return b.overseas - a.overseas })[0]
      window.parent.postMessage({ type: 'cosmo:business-report-analysis', report: { key: 'overseas-chain-revenue', title: '卡奥斯海外链群收入', summary: ['H1整体收入' + this.num(this.totalIncome) + '万元，海外收入' + this.num(this.overseasIncome) + '万元，占整体收入' + (this.totalIncome ? (this.overseasIncome / this.totalIncome * 100).toFixed(1) : '0.0') + '%。', '海外收入目标' + this.num(this.overseasTarget) + '万元，整体完成率' + (this.overseasTarget ? (this.overseasIncome / this.overseasTarget * 100).toFixed(1) : '0.0') + '%。', topRow ? topRow.name + '海外收入最高，为' + this.num(topRow.overseas) + '万元。' : '暂无数据。'], risks: ['海外收入整体完成率偏低，存在目标缺口。', '数据覆盖率不足，部分链群暂无可用数据。'], recommendations: ['对完成率偏低的链群建立海外项目交付与收入确认周清单。', '补齐其余链群的海外收入、目标数据。'], data: { rows: this.rows, totalIncome: this.totalIncome, overseasIncome: this.overseasIncome, overseasTarget: this.overseasTarget }}}, window.location.origin)
    }
  }
}
</script>

<style lang="scss">
.rpt-overseas-chain {
  height: 100%; min-height: 0; padding: 16px 20px;
  background: #eef3f9; overflow-y: auto;
  font-family: "PingFang SC", "HarmonyOS Sans SC", "Microsoft YaHei", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
  scrollbar-width: thin; scrollbar-color: #fff transparent;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #fff; border-radius: 2px; }

  .report-shell { display: flex; height: 100%; max-width: 1600px; margin: 0 auto; flex-direction: column; }

  .report-header { position: relative; overflow: hidden; padding: 18px 22px; border: 1px solid rgba(148,163,184,.28); border-radius: 15px; background: linear-gradient(135deg,#ffffff,#f8fafc); box-shadow: 0 16px 38px rgba(15,23,42,.08); flex-shrink: 0; margin-bottom: 12px;
    &::after { content: ""; position: absolute; right: -80px; top: -100px; width: 260px; height: 260px; border-radius: 50%; background: radial-gradient(circle,rgba(74,95,243,.18),rgba(74,95,243,0) 68%); pointer-events: none; }
  }

  .header-row { position: relative; z-index: 1; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }

  h1 { margin: 0 0 6px; font-size: 24px; line-height: 1.25; letter-spacing: -.02em; color: #0f172a; font-weight: 800; }

  .subtitle { margin: 0; color: #64748b; font-size: 13px; line-height: 1.5; }

  .header-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

  .report-badge { height: 34px; display: inline-flex; align-items: center; gap: 7px; padding: 0 13px; border-radius: 9px; border: 1px solid rgba(148,163,184,.28); background: #fff; color: #64748b; font-size: 12px; font-weight: 700; }

  .analysis-button { height: 34px; display: inline-flex; align-items: center; gap: 7px; padding: 0 14px; border-radius: 9px; border: none; background: linear-gradient(135deg,#4a5ff3,#2d7ff9); color: #fff; font-size: 12px; font-weight: 700; cursor: pointer; box-shadow: 0 7px 16px rgba(74,95,243,.2);
    &:hover { filter: brightness(1.08); }
  }

  .kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin: 0 0 12px; flex-shrink: 0; }

  .kpi-card { position: relative; overflow: hidden; padding: 14px 16px; border: 1px solid rgba(148,163,184,.28); border-radius: 12px; background: #fff; box-shadow: 0 6px 16px rgba(15,23,42,.045);
    &::after { content: ""; position: absolute; right: -24px; bottom: -34px; width: 86px; height: 86px; border-radius: 50%; background: radial-gradient(circle,rgba(74,95,243,.1),transparent 70%); pointer-events: none; }
  }

  .kpi-label { font-size: 12px; color: #64748b; }
  .kpi-value { margin-top: 6px; font-size: 22px; font-weight: 850; letter-spacing: -.02em; }
  .kpi-note { margin-top: 3px; font-size: 10px; color: #64748b; }

  .table-card { display: flex; min-height: 0; flex: 1; overflow: hidden; border: 1px solid rgba(148,163,184,.28); border-radius: 15px; background: #fff; box-shadow: 0 16px 38px rgba(15,23,42,.08); flex-direction: column; }

  .table-card-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding: 12px 16px; border-bottom: 1px solid rgba(148,163,184,.28); flex-shrink: 0; }

  .section-title { display: flex; align-items: center; gap: 9px; font-size: 14px; font-weight: 800; color: #0f172a; }

  .section-note { margin-top: 4px; font-size: 11px; color: #64748b; }

  .legend { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; font-size: 11px; color: #64748b; flex-shrink: 0;
    span { display: inline-flex; align-items: center; gap: 5px; }
  }

  .legend-dot { width: 8px; height: 8px; border-radius: 50%; }

  .table-scroll { min-height: 0; flex: 1; overflow: auto;
    table { width: 100%; min-width: 900px; border-collapse: separate; border-spacing: 0; font-size: 11px; font-variant-numeric: tabular-nums; }
    thead th { position: sticky; top: 0; z-index: 4; color: #fff; text-align: center; font-weight: 800; padding: 8px 6px; border-right: 1px solid rgba(148,163,184,.28); border-bottom: 1px solid rgba(148,163,184,.28); white-space: nowrap; background: #0d5fac; font-size: 11px; }
    .col-chain { width: 14%; }
    .col-num { width: 14%; }
    .col-group { }
    .col-eval { width: 10%; }
    tbody td { padding: 8px 7px; border-right: 1px solid rgba(148,163,184,.28); border-bottom: 1px solid rgba(148,163,184,.28); text-align: center; white-space: nowrap; }
    th:last-child, td:last-child { border-right: 0; }
    tbody tr:last-child td { border-bottom: 0; }
  }

  .chain-name { font-weight: 850; text-align: left !important; }
  .numeric { text-align: right !important; }

  .rate-cell { position: relative; overflow: hidden; }
  .rate-fill { position: absolute; inset: 3px auto 3px 3px; border-radius: 4px; opacity: .12; pointer-events: none; }
  .rate-text { position: relative; z-index: 1; font-weight: 800; }

  .evaluation { display: flex; align-items: center; justify-content: center; gap: 7px; font-size: 11px; font-weight: 800; }
  .status-light { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }

  .report-foot { display: flex; justify-content: space-between; gap: 12px; padding: 8px 14px; border-top: 1px solid rgba(148,163,184,.28); font-size: 10px; color: #64748b; line-height: 1.5; flex-shrink: 0; }
}
</style>
